/**
 * collection of snowplow contexts.
 * json schemas: https://github.com/justwatch/jw-dwh/tree/master/snowplow-schemas/iglu-schemas/schemas/com.justwatch
 */
import type { Beautify, Maybe } from '@/helpers/types';
import type { ClickoutOfferDetails, ClickoutPlacement, ClickoutType } from '@/interfaces/snowplow/clickout-context';
import type { HomeModule } from '@/features/onboarded-home/types';
import type { Metric } from 'web-vitals';
import type { PageEntryMode, SessionEnvironment } from '@/stores/pinia/useTrackingStore';
import type { Route } from 'vue-router';
import type { SingleStepSportEventFragment } from '@/features/sports/graphql/fragments/SingleStepSportEvent.fragment';
import type { MultiStepSportEventFragment } from '@/features/sports/graphql/fragments/MultiStepSportEvent.fragment';
import type { SportCategory } from '@/features/sports/types/enums';
import type { SportType } from '@/@types/graphql-types';
import type { TitleContentType } from '@/interfaces/snowplow/title-context';

import { ClickoutContentType } from '@/interfaces/snowplow/clickout-context';
import { ObjectType, SportEventStatus, SportObjectType } from '@/@types/graphql-types';
import { ScoringProvider, SCORINGS } from '@/helpers/scoring-helper';
import { SportPageType } from '@/features/sports/types/interfaces';
import { TitleObjectType } from '@/interfaces/titles';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';
import { coerceIndex } from '@/helpers/object-helper';
import { getTitleContextEntityId } from '@/helpers/entity-helper';
import { isEmptyArray } from '@/helpers/object-helper';
import { isMultiStepSportEvent } from '@/features/sports/types/enums';
import { toRESTMonetizationType, toRESTPresentationType } from '@/helpers/graphql-model-helper';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';

// looks something like this: 'iglu:com.justwatch/title_context/jsonschema/1-0-0'
export type SnowplowSchema = string;

/**
 * Collection of snowplow context classes that can be created
 *
 * Note: every new Context needs to be added to the CONTEXT_CLASSES object at the end of the file
 */
export abstract class SnowplowContext {
	/**
	 * all SnowplowContexts need to have a __name property for unserialization reasons (server > client)
	 */
	abstract __name: SnowplowContextName;

	toObject() {
		const data: Record<string, any> = {};

		// Include public getters from the prototype
		const descriptors = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(this));
		for (const [key, descriptor] of Object.entries(descriptors)) {
			if (
				typeof descriptor.get === 'function' && // public getters
				!key.startsWith('__') && // exclude __name
				key !== 'schema' // exclude the schema getter which is added after
			) {
				const value = this[key as keyof SnowplowContext];
				if (value != null) {
					data[key] = value;
				}
			}
		}

		// Include public instance fields
		for (const key of Object.keys(this)) {
			if (
				!key.startsWith('__') && // exclude __name
				!(key in data) // don't override any getter-derived keys
			) {
				const value = this[key as keyof SnowplowContext];
				if (value != null) {
					data[key] = value;
				}
			}
		}

		return {
			schema: this.schema,
			data,
		};
	}

	/** Schema is unique for each context. */
	abstract get schema(): SnowplowSchema;
}

export class SnowplowTitleContext extends SnowplowContext {
	__name = 'TitleContext' as const;
	jwEntityId: string;

	constructor(
		public titleId: number,
		public objectType: string,
		public seasonNumber: number | null = null,
		public episodeNumber: number | null = null
	) {
		super();
		this.jwEntityId = getTitleContextEntityId(
			objectType.toLocaleUpperCase() as ObjectType,
			titleId,
			'SnowplowTitleContext'
		);
	}

	get schema() {
		return 'iglu:com.justwatch/title_context/jsonschema/1-0-0';
	}
}

type Movie = {
	__typename: 'Movie';
	id: string;
	objectId: number;
	objectType: ObjectType;
};

type Show = {
	__typename: 'Show';
	id: string;
	objectId: number;
	objectType: ObjectType;
	watchNextEpisode?: {
		content?: {
			episodeNumber?: number;
		};
	} | null;
};

type Season = {
	__typename: 'Season';
	id: string;
	objectId: number;
	objectType: ObjectType;
	content?: {
		seasonNumber?: number;
	};
	show: {
		__typename: 'Show';
		id: string;
		objectId: number;
		objectType: ObjectType;
	};
};

type Episode = {
	__typename: 'Episode';
	id: string;
	objectId: number;
	objectType: ObjectType;
	content?: {
		seasonNumber?: number;
		episodeNumber?: number;
	};
	show: {
		__typename: 'Show';
		id: string;
		objectId: number;
		objectType: ObjectType;
	};
};

export type TitleForContext = Beautify<Movie | Show | Season | Episode>;

export class SnowplowTitleContextGraphql extends SnowplowContext {
	__name = 'TitleContextGraphql' as const;
	jwEntityId: string;

	constructor(
		public titleId: number,
		public objectType: ObjectType,
		public seasonNumber: Maybe<number> = null,
		public episodeNumber: Maybe<number> = null,
		public contentType: Maybe<TitleContentType> = null,
		public index: Maybe<number> = null,
		/** jwRawEntityId is a required to pass the entityId of an offer when it differs from the title EntityId (e.g. seasons, episodes) on clickuts for UCT2. */
		public jwRawEntityId: Maybe<string> = undefined
	) {
		super();

		// Add safety checks on type of index, as we send bad rows when index is not a number or nullish
		this.index = coerceIndex(index);

		this.objectType = objectType?.toLowerCase() as ObjectType;
		this.jwEntityId = getTitleContextEntityId(objectType, titleId, 'SnowplowTitleContextGraphql');
	}

	static fromTitle(
		title: TitleForContext,
		extraInformation?: {
			index?: Maybe<number>;
			contentType?: Maybe<TitleContentType>;
		}
	) {
		const { contentType = null, index = null } = extraInformation ?? {};

		if (title.__typename === 'Episode') {
			if (title.content?.seasonNumber && title.content?.episodeNumber && title.show) {
				return new this(
					title.show.objectId,
					title.show.objectType,
					title.content.seasonNumber ?? null,
					title.content.episodeNumber ?? null,
					contentType,
					index,
					title.show.id
				);
			}
		}

		const objectId = title.__typename === 'Season' ? title.show.objectId : title.objectId;
		const objectType = title.__typename === 'Season' ? title.show.objectType : title.objectType;
		const seasonNumber = title.__typename === 'Season' ? title.content?.seasonNumber : undefined;

		let episodeNumber = null;
		if (title.__typename === 'Episode') episodeNumber = title.content?.episodeNumber;
		if (seasonNumber != null && title.__typename === 'Show')
			episodeNumber = title.watchNextEpisode?.content?.episodeNumber;

		return new this(objectId, objectType, seasonNumber, episodeNumber, contentType, index, title.id);
	}

	get schema() {
		return 'iglu:com.justwatch/title_context/jsonschema/1-3-0';
	}
}

// flex-context 1-0-0 is a generic schema to send valueText, valueInt and valueBool
// this is being used to wrap the user email hash with key e
export class SnowplowHashedLoggedInUserContext extends SnowplowContext {
	__name = 'HashedLoggedInUserContext' as const;
	public key = 'e' as const;
	public valueText: string;

	constructor(hash: string) {
		super();
		this.key;
		this.valueText = hash;
	}

	get schema() {
		return 'iglu:com.justwatch/flex_context/jsonschema/1-0-0';
	}
}

export type SnowplowTitleContextGraphqlPayload = ConstructorParameters<typeof SnowplowTitleContextGraphql>;

export class SnowplowPersonContext extends SnowplowContext {
	__name = 'PersonContext' as const;
	constructor(public personId: number, public fullName: string) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/person_context/jsonschema/1-0-0';
	}
}

export class SnowplowCampaignContextV1 extends SnowplowContext {
	__name = 'CampaignContextV1' as const;
	constructor(
		public network: string,
		public targetUrl: string,
		public targetId: number | null = null,
		public placement: string | null = null,
		public campaignId: number | null = null,
		public adgroupId: number | null = null,
		public creativeId: number | null = null,
		public adId: number | null = null,
		public videoId: number | null = null,
		public keyword: string | null = null,
		public audienceType: string | null = null
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/campaign_context/jsonschema/1-0-0';
	}
}

export class SnowplowCampaignContextV2 extends SnowplowContext {
	__name = 'CampaignContextV2' as const;
	constructor(
		public buyer: string, // uct_b
		public supplier: string, // uct_s
		public inventory: string, // uct_i
		public adType: string, // uct_at
		public targetingName: string, // uct_tn
		public targetUrl: string,
		public placement: string | null = null, // uct_p
		public slotId: number | null = null, // uct_sid
		public campaignId: number | null = null, // uct_cid
		public adgroupId: number | null = null, // uct_adgid
		public creativeId: number | null = null, // uct_crid
		public adId: number | null = null, // uct_adid
		public slotSupplierId: number | null = null, // uct_ssid
		public subcampaignId: number | null = null,
		public eccId: number | null = null
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/campaign_context/jsonschema/2-0-0';
	}
}

export class SnowplowGeoContext extends SnowplowContext {
	__name = 'GeoContext' as const;
	constructor(
		public latitude: number,
		public longitude: number,
		public locator_type: 'geocoding' | 'geolocation',
		public accuracy: number | null = null
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/geo_context/jsonschema/1-0-0';
	}
}

export class SnowplowLoginContext extends SnowplowContext {
	__name = 'LoginContext' as const;
	constructor(public jwLid: string) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/login_context/jsonschema/1-0-0';
	}
}

export class SnowplowSessionEnvironmentContext extends SnowplowContext {
	__name = 'SessionEnvironmentContext' as const;

	public origin: string | null;
	public env: string | null;
	public hasAdblocker: boolean | null;
	public originPageUrl: string | null;
	public originPageReferred: string | null;
	public originRefrMedium: string | null;
	public originRefrSource: string | null;
	public originRefrSocial: string | null;

	constructor(sessionEnvironment: SessionEnvironment) {
		super();
		this.origin = sessionEnvironment.origin;
		this.hasAdblocker = sessionEnvironment.hasAdBlocker;
		this.originPageUrl = sessionEnvironment.originPageUrl;
		this.originPageReferred = sessionEnvironment.originPageReferred;
		this.originRefrMedium = sessionEnvironment.originRefrMedium;
		this.originRefrSource = sessionEnvironment.originRefrSource;
		this.originRefrSocial = sessionEnvironment.originRefrSocial;
		this.env = sessionEnvironment.env;
	}

	get schema() {
		return 'iglu:com.justwatch/session_environment_context/jsonschema/1-1-0';
	}
}

export class SnowplowCMPContext extends SnowplowContext {
	__name = 'CMPContext' as const;
	constructor(public value: string) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/cmp_context/jsonschema/1-0-0';
	}
}

export class SnowplowFreemiumContext extends SnowplowContext {
	__name = 'FreemiumContext' as const;
	constructor(public isHideTitlesFiltered: boolean, public tier: number = 1) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/freemium_context/jsonschema/1-0-0';
	}
}

export class SnowplowPackageContext extends SnowplowContext {
	__name = 'PackageContext' as const;
	constructor(
		public packageId: number,
		public packageName: string,
		public parentType?: null | 'package' | 'bundle',
		public parentId?: number,
		public parentName?: string
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/package_context/jsonschema/1-0-0';
	}
}

// @TODO add logic which defines how the filterSettings are put into context
export class SnowplowFilterContext extends SnowplowContext {
	__name = 'FilterContext' as const;

	isProviderFiltered: boolean;
	providerId: string;
	isGenreFiltered: boolean;
	genreId: string;
	genreIdExcluded: string;
	isAgeRatingFiltered: boolean;
	ageRating: string | null;
	sortedBy: string | null;
	objectTypeFilter: number;
	isMonetizationTypeFiltered: boolean;
	monetizationType: string;
	isScoringFiltered: boolean;
	scoring: string;
	countryCode: string;
	languageCode: string;
	isMyStreamingServicesFiltered: boolean;
	searchString: string | null;
	releaseYearFrom: number | null;
	releaseYearUntil: number | null;
	minPriceCent: number | null;
	maxPriceCent: number | null;
	pageId: number | null;

	// Premium Filters
	isRuntimeFiltered: boolean;
	runtimeFrom: number | null;
	runtimeUntil: number | null;

	isImdbPopularityFiltered: boolean;
	imdbPopularityFrom?: number | null;
	imdbPopularityUntil?: number | null;

	isProductionCountryFiltered: boolean;
	productionCountries: string | null;
	productionCountriesExclude: string | null;

	constructor(
		filterState: FilterCollection,
		routeQuery: Route['query'],
		pageId: number,
		locale: string,
		providerShortNames: string[]
	) {
		super();

		const scoringValues = SCORINGS.filter(item => item.filterDisplay).reduce((root, item) => {
			root[item.provider] = { min_scoring_value: item.min, max_scoring_value: item.max };
			return root;
		}, {} as any);

		if (filterState) {
			// NON-NULLABLE
			this.isProviderFiltered = filterState.providers.length > 0;
			const selectedProviders = this.isProviderFiltered ? filterState['providers'] : providerShortNames;
			this.providerId = this.#getFormattedArrayString(selectedProviders);

			this.isGenreFiltered = filterState.genres?.length > 0 || filterState.exclude_genres?.length > 0;
			this.genreId = this.#getFormattedArrayString(filterState['genres']);
			this.genreIdExcluded = this.#getFormattedArrayString(filterState['exclude_genres']);

			this.isAgeRatingFiltered = filterState.age_certifications?.length > 0;
			this.ageRating = this.#getFormattedArrayString(filterState['age_certifications']);
			this.sortedBy = filterState.sort_by;

			if (filterState.content_types.indexOf(TitleObjectType.MOVIE) !== -1) {
				this.objectTypeFilter = 1;
			} else if (
				filterState.content_types.indexOf(TitleObjectType.SHOW) !== -1 ||
				filterState.content_types.indexOf(TitleObjectType.SEASON) !== -1
			) {
				this.objectTypeFilter = 2;
			} else {
				this.objectTypeFilter = 0;
			}
			this.isMonetizationTypeFiltered = filterState.monetization_types?.length > 0;
			this.monetizationType = this.#getFormattedArrayString(filterState['monetization_types']);
			this.isScoringFiltered = Object.keys(filterState.scoring_filter_types || {}).length > 0;
			this.scoring = this.#getStringifiedArrayValue(filterState, 'scoring_filter_types', scoringValues);
			const localeSplit = locale.split('_');
			this.languageCode = localeSplit[0].toLowerCase();
			this.countryCode = localeSplit[1].toLowerCase();
			// dummy value, as of 2020-12-15 this was only used in jw-tv and
			// did not make sense for jw-app as there's no such filter
			this.isMyStreamingServicesFiltered = false;
			// NULLABLE
			this.searchString = (routeQuery.q || '') as string;
			this.releaseYearFrom = filterState.release_year_from;
			this.releaseYearUntil = filterState.release_year_until;
			this.minPriceCent =
				filterState.min_price || filterState.min_price === 0 ? Math.round(filterState.min_price * 100) : null;
			this.maxPriceCent =
				filterState.max_price || filterState.max_price === 0 ? Math.round(filterState.max_price * 100) : null;
			this.pageId = pageId;

			//Premium Filters
			this.isRuntimeFiltered = !!(filterState.min_runtime || filterState.max_runtime);
			this.runtimeFrom = filterState.min_runtime;
			this.runtimeUntil = filterState.max_runtime;

			this.isImdbPopularityFiltered = !!filterState.scoring_filter_types?.[ScoringProvider.IMDB_VOTES];
			this.imdbPopularityFrom = filterState.scoring_filter_types?.[ScoringProvider.IMDB_VOTES]?.min_scoring_value;
			this.imdbPopularityUntil =
				filterState.scoring_filter_types?.[ScoringProvider.IMDB_VOTES]?.max_scoring_value;

			this.isProductionCountryFiltered =
				filterState.production_countries?.length > 0 || filterState.exclude_production_countries?.length > 0;
			this.productionCountries = this.#getFormattedArrayString(filterState['production_countries']);
			this.productionCountriesExclude = this.#getFormattedArrayString(
				filterState['exclude_production_countries']
			);
		}
	}

	#getStringifiedArrayValue(filterState: FilterCollection, key: keyof FilterCollection, fallback: string[]) {
		const value = ((filterState[key] as string[]) || []).length === 0 ? fallback : filterState[key];
		return JSON.stringify(value);
	}

	#getFormattedArrayString(filterValue: string[]) {
		return isEmptyArray(filterValue) ? '' : filterValue.join(', ');
	}

	get schema() {
		return 'iglu:com.justwatch/filter_context/jsonschema/1-1-4';
	}
}

export class LegacySnowplowPushContext extends SnowplowContext {
	__name = 'LegacyPushContext' as const;
	constructor(
		public pushCampaignId: number,
		public pushId: number,
		public pushRunId: number,
		public dispatchId: string,
		public localizedPlatformMessageId: number,
		public deeplinkTarget: string,
		public deviceLocale: string,
		public appLocale: string,
		public openedTstamp: string | null = null
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/push_context/jsonschema/1-0-0';
	}
}

export class SnowplowPushContext extends SnowplowContext {
	__name = 'PushContext' as const;
	constructor(
		public pushType: string,
		public pushTopic: string,
		public pushId: number,
		public pushRunId: number,
		public notificationId: string,
		public userId: string,
		public templateId: number,
		public deeplinkTarget: string,
		public deviceLocale: string,
		public appLocale: string,
		public sentTstamp: string | null
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/push_context/jsonschema/2-1-0';
	}
}
export class SnowplowClickoutContextGraphql extends SnowplowContext {
	__name = 'ClickoutContextGraphql' as const;
	provider: string | null; // clearName
	providerId: number | undefined;
	monetizationType: string;
	presentationType: string;
	currency: string | undefined;
	priceCent: number | undefined;
	originalPriceCent: number | undefined;
	audioLanguage: string | undefined;
	subtitleLanguage: string | null;
	showtime: string | undefined;
	partnerId: number | undefined;
	contentType: ClickoutContentType | undefined;
	filterOption: BuyBoxFilterOptions | undefined;
	rowNumber: number | undefined;
	deeplink: string | undefined;

	isAffiliated: boolean | undefined;
	isUCTError: boolean | undefined;
	UCTError: string | undefined;

	#deeplinkFallback: string | undefined;

	placement: ClickoutPlacement | undefined;
	clickoutType: ClickoutType | undefined;

	// new addition in 1-6-0: now we can have clickouts from different countries as well (international offers)
	country: string | undefined;
	// new addition in 1-8-0: this means that the title page doesn't have any domestic offers and the foreign offers kick in
	noOffer: boolean | undefined;
	// Tracks the placement index in the swiper
	swiperIndex: number | undefined;

	get deeplinkFallback(): string | undefined {
		return this.#deeplinkFallback;
	}

	// Wanna make sure the link is always encoded wherever it gets set
	set deeplinkFallback(link: string | undefined) {
		this.#deeplinkFallback = link ? encodeURI(link) : link;
	}

	static clone(context: SnowplowClickoutContextGraphql): SnowplowClickoutContextGraphql {
		const instance = new this();
		Object.assign(instance, context);
		instance.deeplinkFallback = context.deeplinkFallback;
		return instance;
	}

	static fromProviderOffer(
		offer: ClickoutOfferDetails,
		contentType?: SportObjectType | ClickoutContentType.LeavingSoon,
		filterOption?: BuyBoxFilterOptions,
		rowNumber?: number,
		placement?: ClickoutPlacement | undefined,
		clickoutType?: ClickoutType | undefined
	) {
		const instance = new this();

		instance.provider = offer.package.clearName;
		instance.monetizationType = offer.monetizationType ? toRESTMonetizationType(offer.monetizationType) : 'unknown';
		instance.presentationType = offer.presentationType ? toRESTPresentationType(offer.presentationType) : 'unknown';
		instance.providerId = offer.package.packageId;
		instance.partnerId = 1;
		instance.contentType = this.getContentType(contentType);
		instance.placement = placement;
		instance.clickoutType = clickoutType;
		if (offer.country) {
			instance.country = offer.country;
		}

		instance.filterOption = filterOption;
		instance.rowNumber = rowNumber;

		if (offer.standardWebURL)
			instance.deeplinkFallback = offer?.preAffiliatedStandardWebURL || offer.standardWebURL;
		if (offer.retailPriceValue) instance.priceCent = Math.round(offer.retailPriceValue * 100);
		if (offer.currency) instance.currency = offer.currency;
		if (offer.lastChangeRetailPriceValue)
			instance.originalPriceCent = Math.round(offer.lastChangeRetailPriceValue * 100);

		return instance;
	}

	static getContentType(
		contentType?: SportObjectType | ClickoutContentType.LeavingSoon
	): ClickoutContentType | undefined {
		switch (contentType) {
			case SportObjectType.MainEvent:
				return ClickoutContentType.MainEvent;
			case SportObjectType.Highlight:
				return ClickoutContentType.Highlight;
			case SportObjectType.Interview:
				return ClickoutContentType.Interview;
			case ClickoutContentType.LeavingSoon:
				return ClickoutContentType.LeavingSoon;
			default:
				return undefined;
		}
	}

	get schema() {
		return 'iglu:com.justwatch/clickout_context/jsonschema/1-10-0';
	}
}

// Use clickout context 1-2-0 for affiliate links
export class SnowplowClickoutContextAffiliateLink extends SnowplowContext {
	__name = 'ClickoutContextAffiliateLink' as const;
	// provider offer clickout
	provider: string | null; // clearName
	providerId: number | undefined;
	monetizationType: string;
	presentationType: string;
	currency: string | undefined;
	price: number | undefined;
	originalPrice: number | undefined;
	// cinema clickout
	audioLanguage: string | undefined;
	subtitleLanguage: string | null;
	showtime: string | undefined;
	partnerId: number | undefined;

	static fromProviderOffer(offer: ClickoutOfferDetails) {
		const instance = new this();

		// @ts-ignore
		const bundleId: number | null = offer.bundleId ?? null;

		instance.provider = offer.package.clearName;
		instance.monetizationType = offer.monetizationType ? toRESTMonetizationType(offer.monetizationType) : 'unknown';
		instance.presentationType = offer.presentationType ? toRESTPresentationType(offer.presentationType) : 'unknown';
		instance.providerId = bundleId ?? offer.package.packageId;
		instance.partnerId = 1;

		if (offer.retailPriceValue) instance.price = offer.retailPriceValue;
		if (offer.currency) instance.currency = offer.currency;
		if (offer.lastChangeRetailPriceValue) instance.originalPrice = offer.lastChangeRetailPriceValue;

		return instance;
	}

	get schema() {
		return 'iglu:com.justwatch/clickout_context/jsonschema/1-2-0';
	}
}

export class SnowplowQuestionContext extends SnowplowContext {
	__name = 'QuestionContext' as const;
	constructor(
		public surveyId: number,
		public surveySession: string,
		public sequenceNumber: number,
		public nodeId: number,
		public elementId: number,
		public subtype: string,
		public translationId: number,
		public variationId: number,
		public locale: string, // 5 char locale e.g. de_DE
		public tagId?: number
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/question_context/jsonschema/1-0-0';
	}
}

export class SnowplowAnswerContext extends SnowplowContext {
	__name = 'AnswerContext' as const;
	constructor(
		public isSelected: boolean,
		public answerIndex: number,
		public nodeId: number,
		public elementId: number,
		public subtype: 'simple' | 'free_text',
		public translationId: number,
		public variationId: number,
		public textualAnswer?: string
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/answer_context/jsonschema/1-0-0';
	}
}

export class SnowplowIntentContext extends SnowplowContext {
	__name = 'IntentContext' as const;
	constructor(public id: number, public technicalName: string) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/intent_context/jsonschema/1-0-0';
	}
}

export class SnowplowOldModuleContext extends SnowplowContext {
	__name = 'OldModuleContext' as const;
	constructor(
		public moduleId: string,
		public moduleIdx: number,
		public templateTechnicalName: string,
		public discoverySessionId: string,
		public anchorValue?: string | null
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/module_context/jsonschema/1-0-0';
	}
}

export class SnowplowModuleContext extends SnowplowContext {
	__name = 'ModuleContext' as const;

	/** Type of the module. */
	type: string;

	/** Sub type of the module, if applicable. */
	subType: string | null = null;

	/**
	 * ID of the entity that we are using to show the module content.
	 * List ID, Article ID, Person ID, whatever that can help to link to content object behind...
	 */
	objectId: string | null = null;

	/**
	 * Locally generated uuid of the module.
	 * To be used later for interlinking and influenced tracking.
	 */
	sessionUuid: string;

	/** Global row index position on the page (rowIdx). */
	globalPosition: number;

	/** Local horizontal index within the row (horizIdx). */
	localPosition: number | null = null;

	/** Position of a module relative to other modules from the same group (moduleID). */
	groupPosition: number | null = null;

	/** Number of modules and containers at top/global level. */
	globalSize: number | null = null;

	/** Number of modules in the module's current container whenever applicable (horizSize). */
	localSize: number | null = null;

	/** Total number of titles in a module showing multiple titles. */
	titleSize: number | null = null;

	/**
	 * Type of the parent module if the module is in a container (rotator, spotlight).
	 * Null if the module is not in a container.
	 */
	containerType: string | null = null;

	constructor(
		moduleData: NonNullable<HomeModule>,
		{
			objectId = null,
			globalPosition,
			localPosition = null,
			globalSize,
			localSize = null,
			titleSize = null,
			containerType = null,
		}: {
			objectId?: string | null;
			globalPosition: number;
			localPosition?: number | null;
			globalSize: number;
			localSize?: number | null;
			titleSize?: number | null;
			containerType?: string | null;
		}
	) {
		super();

		this.type = moduleData.type;
		this.subType = moduleData.category;
		this.containerType = containerType;

		this.sessionUuid = crypto.randomUUID();
		this.globalPosition = globalPosition;
		this.localPosition = localPosition;
		this.groupPosition = moduleData.moduleID;

		this.globalSize = globalSize;
		this.localSize = localSize;
		this.titleSize = titleSize;

		this.objectId = objectId;
	}

	get schema() {
		return 'iglu:com.justwatch/module_context/jsonschema/2-0-0';
	}
}

export class SnowplowImpressionContext extends SnowplowContext {
	__name = 'ImpressionContext' as const;
	titles: string;

	constructor(public length: number, public indexOffset: number, entityTitles: string[], public seenUntil?: number) {
		super();

		this.titles = JSON.stringify(entityTitles);
	}

	get schema() {
		return 'iglu:com.justwatch/impression_context/jsonschema/1-0-0';
	}
}

export class SnowplowImpressionContext2 extends SnowplowContext {
	__name = 'ImpressionContext2' as const;
	constructor(public titleCodes: string[], public arrayLength: number) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/impression_context/jsonschema/2-0-0';
	}
}

export class SnowplowPageTypeContext extends SnowplowContext {
	__name = 'PageTypeContext' as const;
	constructor(
		public pageType: string,
		public appLocale: string,
		public appLanguage: string,
		public pageViewUuid: string | null,
		public pageEntryMode: PageEntryMode | null = null
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/page_type_context/jsonschema/1-1-0';
	}
}

// CHANGE identifier VALUE IF NEEDED
export class SnowplowDevelopmentTestContext extends SnowplowContext {
	__name = 'DevelopmentTestContext' as const;
	constructor(public testEvent: boolean, public identifier: string = 'staging-environment') {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/development_test_context/jsonschema/1-0-0';
	}
}

/**
 * Snowplow experiment context
 */
export class SnowplowExperimentContext extends SnowplowContext {
	__name = 'ExperimentContext' as const;
	constructor(
		public experimentName: string,
		public experimentIdShort: string | null = null,
		public version: string,
		public variant: string,
		public status: 'ACTIVE' | 'ACQUIRED' | null = null,
		public description: string = ''
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/experiment_context/jsonschema/1-2-0';
	}
}

export class SnowplowSportContext extends SnowplowContext {
	__name = 'SportContext' as const;
	sportTechName: SportType | SportCategory;
	// https://gitlab.justwatch.com/jw/jw-dwh/-/blob/master/snowplow-schemas/iglu-schemas/schemas/com.justwatch/sport_context/jsonschema/1-0-1#L17
	objectType: 'sport' | 'competition' | 'season' | 'event' | 'venue' | 'competitor' | 'generic';
	competitionId: number | undefined;
	seasonId: number | undefined;
	eventId: number | undefined;
	event:
		| {
				status: string;
				matchStatus?: string;
				startAt?: string;
				endAt?: string | null;
				winnerId?: string | undefined; // nice to have
		  }
		| undefined;
	venueId: number | undefined; // nice to have
	competitorIdHome: string | undefined; // nice to have
	competitorIdGuest: string | undefined; // nice to have
	competitorIdWinner: string | undefined; // nice to have

	// competitors: Competitor[]; // future
	constructor(sportTechName: SnowplowSportContext['sportTechName'], rawObjectType: ObjectType | SportPageType) {
		super();
		switch (rawObjectType) {
			case ObjectType.SportCompetitor:
				this.objectType = 'competitor';
				break;
			case SportPageType.SportCompetition:
			case ObjectType.SportCompetition:
				this.objectType = 'competition';
				break;
			case SportPageType.SportEvent:
			case ObjectType.SportEvent:
				this.objectType = 'event';
				break;
			case SportPageType.SportOverview:
			case ObjectType.Sport:
				this.objectType = 'sport';
				break;
			case SportPageType.AllSports:
			default:
				this.objectType = 'generic';
				break;
		}

		this.sportTechName = sportTechName;
	}

	get schema() {
		return 'iglu:com.justwatch/sport_context/jsonschema/1-0-1';
	}

	static getEventContent(event: SingleStepSportEventFragment | MultiStepSportEventFragment) {
		try {
			if (isMultiStepSportEvent(event)) {
				return (
					(event.parent?.content as NonNullable<MultiStepSportEventFragment['parent']>['content']) ??
					(event.content as MultiStepSportEventFragment['content'])
				);
			}

			return event.content as SingleStepSportEventFragment['content'];
		} catch (e) {
			console.log({ e, event });
		}
	}

	static fromSportEventV2(
		sportTechName: SportType | SportCategory,
		objectType: ObjectType | SportPageType,
		event: SingleStepSportEventFragment | MultiStepSportEventFragment
	) {
		const sportContext: SnowplowSportContext | null = new SnowplowSportContext(sportTechName, objectType);
		const eventContent = SnowplowSportContext.getEventContent(event);

		sportContext.competitionId = event.competition?.objectId;
		sportContext.seasonId = event.competition?.activeSeason?.seasonID;
		sportContext.event = {
			status: eventContent?.status ?? SportEventStatus.NotStarted,
			startAt: eventContent?.startAt,
			endAt: eventContent && 'endAt' in eventContent ? eventContent?.endAt : undefined,
			matchStatus: eventContent?.status,
		};
		sportContext.eventId = event.objectId;
		sportContext.venueId =
			eventContent && 'venue' in eventContent ? eventContent?.venue?.venueID || undefined : undefined; // to prevent sending venueID = 0

		if ('competitors' in event) {
			sportContext.competitorIdHome = event.competitors?.homeTeam?.competitors.map(c => c.objectId).join(', ');
			sportContext.competitorIdGuest = event.competitors?.awayTeam?.competitors.map(c => c.objectId).join(', ');

			if (event.competitors.homeTeam.winner) {
				sportContext.event.winnerId = sportContext.competitorIdHome;
				sportContext.competitorIdWinner = sportContext.competitorIdHome;
			}

			if (event.competitors.awayTeam.winner) {
				sportContext.event.winnerId = sportContext.competitorIdGuest;
				sportContext.competitorIdWinner = sportContext.competitorIdGuest;
			}
		}

		return sportContext;
	}
}

function clamp(value: number, min: number, max: number) {
	return Math.max(Math.min(value, max), min);
}

export class SnowplowCoreWebVitalsContext extends SnowplowContext {
	__name = 'CoreWebVitalsContext' as const;
	metrics: readonly {
		name: 'CLS' | 'FCP' | 'FID' | 'INP' | 'LCP' | 'TTFB';
		id: string;
		delta: number;
		value: number;
	}[];

	constructor(data: Set<Metric>, public flags: Record<string, boolean> | null = null) {
		super();

		this.metrics = Object.freeze(
			Array.from(data).map(({ id, name, value, delta }) => ({
				id,
				name,
				value: clamp(Math.round(value * 100) / 100, 0, 99999999),
				delta: Math.round(name === 'CLS' ? delta * 1000 : delta),
			}))
		);
	}

	get schema() {
		return 'iglu:com.justwatch/core_web_vitals_context/jsonschema/1-0-0';
	}
}

export class SnowplowAudienceContext extends SnowplowContext {
	__name = 'AudienceContext' as const;

	constructor(
		public type: string,
		public objectUuid?: string | null,
		public objectId?: string | null,
		// subType can take type string, but for now we are only using it to determine if PA is exisiting (old) or new (fresh)
		public subType?: 'old' | 'fresh' | null
	) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/audience_context/jsonschema/1-0-2';
	}
}

/** The LinkContext contains the UUID(s) of anevent, e.g. impression or clickout.
 * self - the uuid of the event itself,
 * impression - the uuid of a previous impression event that interlinks with e.g. a clickout event
 */

type LinkContextLink = { self: string; impression?: string; clickout?: string };

export class SnowplowLinkContext extends SnowplowContext {
	__name = 'LinkContext' as const;
	link: LinkContextLink;

	constructor({ self, impression, clickout }: LinkContextLink) {
		super();
		this.link = { self, impression, clickout };
	}

	get schema() {
		return 'iglu:com.justwatch/link_context/jsonschema/1-0-0';
	}
}

type LinkToContextLink = { impression?: string; clickout?: string; trailer?: string };
/**
 * The LinkToContext contains the Clickout UUID which is extracted from the original LinkContext.
 * this way BI is able to link follow-up events to the source clickout event.
 */
export class SnowplowLinkToContext extends SnowplowContext {
	__name = 'LinkToContext' as const;
	link: LinkToContextLink;

	constructor({ impression, clickout, trailer }: LinkToContextLink) {
		super();
		this.link = {
			impression,
			clickout,
			trailer,
		};
	}

	get schema() {
		return 'iglu:com.justwatch/link_context/jsonschema/1-0-0';
	}
}

export class SnowplowRecommenderContext extends SnowplowContext {
	__name = 'RecommenderContext' as const;

	constructor(
		public source: string,
		public source_title: string | null = null,
		public source_season_number: number | null = null,
		public influenced: boolean | null = null,
		public composition_id: string | null = null,
		public minScore: number | null = null,
		public maxScore: number | null = null,
		public avgScore: number | null = null,
		public rawScore: number | null = null
	) {
		super();

		// Round scores to 4 decimal places - this is the most allowed by the schema
		this.minScore = this.#roundScore(minScore);
		this.maxScore = this.#roundScore(maxScore);
		this.avgScore = this.#roundScore(avgScore);
		this.rawScore = this.#roundScore(rawScore);
	}

	#roundScore(score: number | null): number | null {
		return score != null ? Number(score.toFixed(4)) : null;
	}

	get schema() {
		return 'iglu:com.justwatch/recommender_context/jsonschema/1-1-2';
	}
}

export class SnowplowSearchContext extends SnowplowContext {
	__name = 'SearchContext' as const;

	#searchEntry: string | null = null;
	#searchEntries: string[] | null = null;

	constructor(
		/** UUID of the search session. */
		public sessionUuid: string,

		/** UUID of the previous search session, if known. */
		public prevSessionUuid: string | null = null,

		/** Active search input text. */
		searchEntry: string | null = null,

		/** An array containing the history of search strings. First element - most recent one. */
		searchEntries: string[] | null = null
	) {
		super();

		this.searchEntry = searchEntry;
		this.searchEntries = searchEntries;
	}

	get searchEntry() {
		return this.#searchEntry;
	}

	set searchEntry(entry: string | null) {
		this.#searchEntry = entry != null ? entry.trim().slice(0, 254) : null;
	}

	get searchEntries() {
		return this.#searchEntries;
	}

	set searchEntries(entries: string[] | null) {
		if (entries == null) return;

		this.#searchEntries = entries.map(entry => entry.trim().slice(0, 254));
	}

	addSearchEntry(entry: string | null) {
		if (entry == null) return;

		/**
		 * Snowplow has a 255 character limit in the schema.
		 * People are just pasting whatever in here.
		 */
		const safeEntry = entry.trim().slice(0, 254);

		this.#searchEntry = safeEntry;

		if (this.#searchEntries == null) this.#searchEntries = [];
		this.#searchEntries.unshift(safeEntry);
	}

	get schema() {
		return 'iglu:com.justwatch/search_context/jsonschema/1-0-0';
	}
}

export class SnowplowInteractionContext extends SnowplowContext {
	__name = 'InteractionContext' as const;

	constructor(public isActive: boolean) {
		super();
	}

	get schema() {
		return 'iglu:com.justwatch/interaction_context/jsonschema/1-0-0';
	}
}

// flex-context 1-0-0 is a generic schema to send valueText, valueInt and valueBool
// this is being used to wrap the user email hash with key e
export class SnowplowFlexContext extends SnowplowContext {
	__name = 'FlexContext' as const;
	public key?: string;
	public valueText?: string;
	public valueInt?: number;
	public valueBool?: boolean;
	public data?: Object;
	public dataArray?: Array<any>;

	constructor(payload: {
		key?: string;
		valueText?: string;
		valueInt?: number;
		valueBool?: boolean;
		data?: Object;
		dataArray?: Array<any>;
	}) {
		super();
		this.key = payload.key;
		this.valueText = payload.valueText;
		this.valueInt = payload.valueInt;
		this.valueBool = payload.valueBool;
		this.data = payload.data;
		this.dataArray = payload.dataArray;
	}

	get schema() {
		return 'iglu:com.justwatch/flex_context/jsonschema/2-0-0';
	}
}

/** Mapping of Snowplow context names and their Class. */
export const CONTEXT_CLASSES = {
	TitleContext: SnowplowTitleContext,
	TitleContextGraphql: SnowplowTitleContextGraphql,
	PersonContext: SnowplowPersonContext,
	CampaignContextV1: SnowplowCampaignContextV1,
	CampaignContextV2: SnowplowCampaignContextV2,
	GeoContext: SnowplowGeoContext,
	LoginContext: SnowplowLoginContext,
	CMPContext: SnowplowCMPContext,
	FreemiumContext: SnowplowFreemiumContext,
	PackageContext: SnowplowPackageContext,
	FilterContext: SnowplowFilterContext,
	LegacyPushContext: LegacySnowplowPushContext,
	PushContext: SnowplowPushContext,
	ClickoutContextGraphql: SnowplowClickoutContextGraphql,
	ClickoutContextAffiliateLink: SnowplowClickoutContextAffiliateLink,
	QuestionContext: SnowplowQuestionContext,
	AnswerContext: SnowplowAnswerContext,
	IntentContext: SnowplowIntentContext,
	OldModuleContext: SnowplowOldModuleContext,
	ModuleContext: SnowplowModuleContext,
	ImpressionContext: SnowplowImpressionContext,
	ImpressionContext2: SnowplowImpressionContext2,
	PageTypeContext: SnowplowPageTypeContext,
	DevelopmentTestContext: SnowplowDevelopmentTestContext,
	ExperimentContext: SnowplowExperimentContext,
	SportContext: SnowplowSportContext,
	CoreWebVitalsContext: SnowplowCoreWebVitalsContext,
	AudienceContext: SnowplowAudienceContext,
	RecommenderContext: SnowplowRecommenderContext,
	HashedLoggedInUserContext: SnowplowHashedLoggedInUserContext,
	SessionEnvironmentContext: SnowplowSessionEnvironmentContext,
	SearchContext: SnowplowSearchContext,
	LinkContext: SnowplowLinkContext,
	LinkToContext: SnowplowLinkToContext,
	InteractionContext: SnowplowInteractionContext,
	FlexContext: SnowplowFlexContext,
};

/** The unique string names for each Snowplow Context. */
export type SnowplowContextName = keyof typeof CONTEXT_CLASSES;

/** All Snowplow contexts and their name. */
export type ContextClasses = {
	[K in SnowplowContextName]: InstanceType<(typeof CONTEXT_CLASSES)[K]>;
};
